import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { FilterItem } from 'types/FilterBuilder'

type SavedFilter = {
  originalFilterName?: string
  filterName?: string
  filterId?: string
}
interface FilterBuilderProps {
  selectedFilters: FilterItem[]
  segmentId: string
  showEmailDialog: boolean
  showScreenNameDialog: boolean
  showLastSeenDialog: boolean
  hasFilterValueChanged: boolean
  showJoinDateDialog: boolean
  showFilterByProjectAndEngagementDialog: boolean
  savedSelectionFilters?: FilterItem[]
  savedFilter?: SavedFilter
  isFiltersetVisible?: boolean
  isSharable?: boolean
  isSaveFilterActive?: boolean
  isSharedModalFlow?: boolean
}

const initialState: FilterBuilderProps = {
  selectedFilters: [],
  segmentId: '',
  showEmailDialog: false,
  showScreenNameDialog: false,
  showLastSeenDialog: false,
  hasFilterValueChanged: false,
  showJoinDateDialog: false,
  showFilterByProjectAndEngagementDialog: false,
  savedSelectionFilters: [],
  savedFilter: {
    originalFilterName: '',
    filterName: '',
    filterId: '',
  },
  isFiltersetVisible: false,
  isSharable: false,
  isSaveFilterActive: false,
  isSharedModalFlow: false,
}

interface UpdateFilterItem {
  index: number
  item: FilterItem
}

export const filterBuilder = createSlice({
  name: 'filterBuilder',
  initialState,
  reducers: {
    setSavedFilterName(state, action: PayloadAction<SavedFilter>) {
      return {
        ...state,
        savedFilter: {
          ...state.savedFilter,
          ...action.payload,
        },
      }
    },
    setSavedSelectionFilters(state, action: PayloadAction<FilterItem[]>) {
      state.savedSelectionFilters = action.payload
    },
    setFilters(state, action: PayloadAction<FilterItem[]>) {
      state.selectedFilters = action.payload
    },
    updateFilter(state, action: PayloadAction<UpdateFilterItem>) {
      const cloneSelectedFilter = [...state.selectedFilters]
      cloneSelectedFilter[action.payload.index] = {
        ...cloneSelectedFilter[action.payload.index],
        ...action.payload.item,
      }

      state.selectedFilters = cloneSelectedFilter
      state.hasFilterValueChanged = true
    },
    addFilter(state, action: PayloadAction<FilterItem>) {
      const cloneSelectedFilter = [...state.selectedFilters]
      cloneSelectedFilter.push(action.payload)

      state.selectedFilters = cloneSelectedFilter
      state.hasFilterValueChanged = false
    },
    addFilterWithValue(state, action: PayloadAction<FilterItem>) {
      const cloneSelectedFilter = [...state.selectedFilters]
      cloneSelectedFilter.push(action.payload)

      state.selectedFilters = cloneSelectedFilter
      state.hasFilterValueChanged = true
    },
    setSegmentId(state, action) {
      state.segmentId = action.payload
    },
    resetFilters(state) {
      const cloned = [...state.selectedFilters]
      const selectedFilters = cloned
        .filter((filter) => filter.isSelected)
        .map((filter) => {
          return { ...filter, defaultOpen: false }
        })
      state.selectedFilters = selectedFilters
    },
    toggleFilterByEmailDialog(state) {
      state.showEmailDialog = !state.showEmailDialog
    },
    toggleFilterByScreenNameDialog(state) {
      state.showScreenNameDialog = !state.showScreenNameDialog
    },
    toggleFilterByLastSeenDialog(state) {
      state.showLastSeenDialog = !state.showLastSeenDialog
    },
    toggleHasFilterValueStatus(state, action) {
      state.hasFilterValueChanged = action.payload
    },
    toggleFilterByJoinDateDialog(state) {
      state.showJoinDateDialog = !state.showJoinDateDialog
    },
    // this is to control the modal opening and closing on click of the items in filter menu
    toggleFilterByProjectAndEngagementDialog(state) {
      state.showFilterByProjectAndEngagementDialog =
        !state.showFilterByProjectAndEngagementDialog
    },
    setFilterVisibility(state, action: PayloadAction<any>) {
      state.isFiltersetVisible = action.payload
    },
    setSharableFilterLink(state, action: PayloadAction<any>) {
      state.isSharable = action.payload
    },
    setSaveFilterActive(state, action: PayloadAction<any>) {
      state.isSaveFilterActive = action.payload
    },
    setSharedModalFlow(state, action: PayloadAction<any>) {
      state.isSharedModalFlow = action.payload
    },
  },
})

export const {
  resetFilters,
  setFilters,
  addFilter,
  addFilterWithValue,
  updateFilter,
  setSegmentId,
  toggleFilterByEmailDialog,
  toggleFilterByScreenNameDialog,
  toggleFilterByLastSeenDialog,
  toggleHasFilterValueStatus,
  toggleFilterByJoinDateDialog,
  toggleFilterByProjectAndEngagementDialog,
  setSavedSelectionFilters,
  setSavedFilterName,
  setFilterVisibility,
  setSharableFilterLink,
  setSaveFilterActive,
  setSharedModalFlow,
} = filterBuilder.actions
export default filterBuilder.reducer
